import { api } from './client';
import type {
  BulkInventoryAdjustmentDto,
  BulkOperationDto,
  BulkPriceUpdateDto,
  CarrierDto,
  CarrierUpsertDto,
  CustomerDetailDto,
  CustomerGroupDto,
  CustomerListItemDto,
  CustomerQueryDto,
  CreateStaffUserDto,
  DashboardSummaryDto,
  DiscountDto,
  DiscountQueryDto,
  DiscountUpsertDto,
  QuickAssignCouponDto,
  GiftCardAdjustDto,
  GiftCardCreateDto,
  GiftCardDto,
  GiftCardQueryDto,
  InventoryAdjustmentDto,
  InventoryCountDto,
  InventoryItemDto,
  InventoryLevelDto,
  InventoryMovementDto,
  InventoryMovementQueryDto,
  InventoryQueryDto,
  LocationDto,
  LocationUpsertDto,
  LowStockAlertDto,
  PagedRequest,
  PagedResult,
  RoleDto,
  SalesTrendPointDto,
  SetUserActiveDto,
  ShippingZoneDto,
  ShippingZoneUpsertDto,
  StaffUserDto,
  StoreSettingDto,
  TaxClassDto,
  TaxClassUpsertDto,
  TopProductDto,
  TopCategoryDto,
  RecentOrderSummaryDto,
  AbandonedCartDetailDto,
  UpdateCustomerAdminDto,
  UpdateSettingsDto,
  UpdateUserRolesDto,
  AdminProductQueryDto,
  OrderQueryDto,
  SalesReportDto,
  ProductReportDto,
  CustomerReportDto,
  ConversionFunnelDto,
  KolayGelsinSettingsDto,
  KolayGelsinSettingsUpsertDto,
  KolayGelsinDispatchResultDto,
  KolayGelsinTrackingResultDto,
  KolayGelsinSyncSummaryDto,
  KolayGelsinReturnCodeResultDto,
  TurkeyCityDto,
  TurkeyDistrictDto,
  EmailTemplateDto,
  EmailTemplateUpsertDto,
  TestEmailTemplateRequestDto,
} from './types';

/* ------------------------------------------------------------------ *
 *  Isletme uclari — 67 uc
 *  stok (11), indirim (6), hediye karti (4), kargo (8), vergi (4),
 *  musteri (4), personel (6), pano (8), ayarlar (2), toplu islem (8),
 *  webhook (5), gunlukler (3)
 * ------------------------------------------------------------------ */

export const adminInventoryApi = {
  /** 1. Stok listesi: varyant basina elde / rezerve / taahhut / satilabilir. */
  search: (query: InventoryQueryDto = {}) =>
    api.get<PagedResult<InventoryItemDto>>('/api/admin/inventory', query),

  /** 2. Tek varyantin tum konumlardaki stogu. */
  byVariant: (variantId: number) =>
    api.get<InventoryItemDto>(`/api/admin/inventory/variants/${variantId}`),

  /** 3. Stok defteri: her hareketin turu, farki ve sonrasi degerleri. */
  movements: (query: InventoryMovementQueryDto = {}) =>
    api.get<PagedResult<InventoryMovementDto>>('/api/admin/inventory/movements', query),

  /** 4. Kritik seviyenin altina dusen urunler. */
  lowStock: (take = 100) =>
    api.get<InventoryItemDto[]>('/api/admin/inventory/low-stock', { take }),

  /** 5. Elle stok duzeltmesi; deftere gerekce ile yazilir. */
  adjust: (dto: InventoryAdjustmentDto) =>
    api.post<InventoryLevelDto>('/api/admin/inventory/adjust', dto),

  /** 6. Sayim sonucunu uygular: fark kadar hareket olusur. */
  applyCount: (dto: InventoryCountDto) =>
    api.post<InventoryLevelDto>('/api/admin/inventory/count', dto),

  /** 7. Coklu varyant icin tek islemde duzeltme. */
  bulkAdjust: (dto: BulkInventoryAdjustmentDto) =>
    api.postCommand('/api/admin/inventory/bulk-adjust', dto),

  /** 8. Depo / magaza konumlari. */
  locations: () => api.get<LocationDto[]>('/api/admin/inventory/locations'),

  /** 9. Konum olusturur. */
  createLocation: (dto: LocationUpsertDto) =>
    api.post<LocationDto>('/api/admin/inventory/locations', dto),

  /** 10. Konum gunceller. */
  updateLocation: (id: number, dto: LocationUpsertDto) =>
    api.put<LocationDto>(`/api/admin/inventory/locations/${id}`, dto),

  /** 11. Konum siler. Stogu varsa reddedilir. */
  deleteLocation: (id: number) => api.deleteCommand(`/api/admin/inventory/locations/${id}/delete`),
};

export const adminDiscountsApi = {
  /** 12. Indirim listesi. */
  search: (query: DiscountQueryDto = {}) =>
    api.get<PagedResult<DiscountDto>>('/api/admin/discounts', query),

  /** 13. Indirim detayi. */
  getById: (id: number) => api.get<DiscountDto>(`/api/admin/discounts/${id}`),

  /** 14. Indirim olusturur. */
  create: (dto: DiscountUpsertDto) => api.post<DiscountDto>('/api/admin/discounts', dto),

  /** 15. Indirim gunceller. */
  update: (id: number, dto: DiscountUpsertDto) =>
    api.put<DiscountDto>(`/api/admin/discounts/${id}`, dto),

  /** 16. Indirim siler. */
  remove: (id: number) => api.deleteCommand(`/api/admin/discounts/${id}/delete`),

  /** 17. Indirim durumunu aktif/pasif degistirir (toggle). */
  toggleStatus: (id: number) =>
    api.patch<DiscountDto>(`/api/admin/discounts/${id}/toggle-status`),

  /** 18. Tek kullanimlik kod uretir (kampanya dagitimi icin). */
  generateCodes: (id: number, count = 100) =>
    api.post<string[]>(`/api/admin/discounts/${id}/generate-codes?count=${count}`),

  /** 19. Müşteriye özel hızlı kupon tanımlar. */
  quickAssign: (dto: QuickAssignCouponDto) =>
    api.post<DiscountDto>('/api/admin/discounts/quick-assign', dto),
};

export const adminGiftCardsApi = {
  /** 18. Hediye karti listesi. */
  search: (query: GiftCardQueryDto = {}) =>
    api.get<PagedResult<GiftCardDto>>('/api/admin/gift-cards', query),

  /** 19. Hediye karti olusturur; kod yalnizca bu yanitta gorunur. */
  create: (dto: GiftCardCreateDto) => api.post<GiftCardDto>('/api/admin/gift-cards', dto),

  /** 20. Bakiye ekler veya duser. */
  adjust: (id: number, dto: GiftCardAdjustDto) =>
    api.post<GiftCardDto>(`/api/admin/gift-cards/${id}/adjust`, dto),

  /** 21. Karti kullanima kapatir. */
  disable: (id: number) => api.postCommand(`/api/admin/gift-cards/${id}/disable`),
};

export const adminShippingApi = {
  /** 22. Kargo bolgeleri ve ucret kurallari. */
  zones: () => api.get<ShippingZoneDto[]>('/api/admin/shipping/zones'),

  /** 23. Bolge olusturur. */
  createZone: (dto: ShippingZoneUpsertDto) =>
    api.post<ShippingZoneDto>('/api/admin/shipping/zones', dto),

  /** 24. Bolge gunceller. */
  updateZone: (id: number, dto: ShippingZoneUpsertDto) =>
    api.put<ShippingZoneDto>(`/api/admin/shipping/zones/${id}`, dto),

  /** 25. Bolge siler. */
  deleteZone: (id: number) => api.deleteCommand(`/api/admin/shipping/zones/${id}/delete`),

  /** 26. Anlasmali kargo firmalari. */
  carriers: () => api.get<CarrierDto[]>('/api/admin/shipping/carriers'),

  /** 27. Kargo firmasi ekler; API anahtari sifreli saklanir. */
  createCarrier: (dto: CarrierUpsertDto) =>
    api.post<CarrierDto>('/api/admin/shipping/carriers', dto),

  /** 28. Kargo firmasini gunceller. */
  updateCarrier: (id: number, dto: CarrierUpsertDto) =>
    api.put<CarrierDto>(`/api/admin/shipping/carriers/${id}`, dto),

  /** 29. Kargo firmasini siler. */
  deleteCarrier: (id: number) => api.deleteCommand(`/api/admin/shipping/carriers/${id}/delete`),
};

export const adminKolayGelsinApi = {
  /** Kolay Gelsin entegrasyon ayarlarını getirir. */
  getSettings: () => api.get<KolayGelsinSettingsDto>('/api/admin/shipping/kolay-gelsin/settings'),

  /** Kolay Gelsin ayarlarını günceller. */
  updateSettings: (dto: KolayGelsinSettingsUpsertDto) =>
    api.put<KolayGelsinSettingsDto>('/api/admin/shipping/kolay-gelsin/settings', dto),

  /** Kolay Gelsin bağlantısını canlı test eder. */
  testConnection: () => api.post<string>('/api/admin/shipping/kolay-gelsin/test-connection'),

  /** Tüm aktif kargoları Kolay Gelsin ile senkronize eder. */
  sync: () => api.post<KolayGelsinSyncSummaryDto>('/api/admin/shipping/kolay-gelsin/sync'),

  /** Sevkiyat için Kolay Gelsin sevk emri ve takip numarası üretir. */
  dispatch: (fulfillmentId: number) =>
    api.post<KolayGelsinDispatchResultDto>(`/api/admin/shipping/kolay-gelsin/fulfillments/${fulfillmentId}/dispatch`),

  /** Kargo etiketi / barkod verisi alır. */
  getBarcode: (fulfillmentId: number) =>
    api.get<any>(`/api/admin/shipping/kolay-gelsin/fulfillments/${fulfillmentId}/barcode`),

  /** Kargo sevk emrini iptal eder. */
  cancel: (fulfillmentId: number) =>
    api.postCommand(`/api/admin/shipping/kolay-gelsin/fulfillments/${fulfillmentId}/cancel`),

  /** Takip numarası ile anlık durum sorgular. */
  getTracking: (trackingNumber: string) =>
    api.get<KolayGelsinTrackingResultDto>(`/api/admin/shipping/kolay-gelsin/tracking/${trackingNumber}`),

  /** İade talebi için Kolay Gelsin iade takip kodu üretir. */
  generateReturnCode: (returnRequestId: number) =>
    api.post<KolayGelsinReturnCodeResultDto>(`/api/admin/shipping/kolay-gelsin/returns/${returnRequestId}/generate-code`),

  /** İade takip numarası durumunu sorgular. */
  getReturnTracking: (returnTrackingNumber: string) =>
    api.get<KolayGelsinTrackingResultDto>(`/api/admin/shipping/kolay-gelsin/returns/${returnTrackingNumber}/tracking`),

  /** Türkiye illerini getirir. */
  getCities: () => api.get<TurkeyCityDto[]>('/api/admin/shipping/kolay-gelsin/cities'),

  /** Seçilen ile ait ilçeleri getirir. */
  getDistricts: (cityId: number) =>
    api.get<TurkeyDistrictDto[]>(`/api/admin/shipping/kolay-gelsin/districts/${cityId}`),

  /** Gönderi ölçü ve desisini günceller. */
  updateMeasurements: (fulfillmentId: number, dto: { weightKg: number; deci: number; widthCm?: number; lengthCm?: number; heightCm?: number }) =>
    api.postCommand(`/api/admin/shipping/kolay-gelsin/fulfillments/${fulfillmentId}/measurements`, dto),
};

export const adminTaxApi = {
  /** 30. Vergi siniflari ve oranlari. */
  classes: () => api.get<TaxClassDto[]>('/api/admin/tax/classes'),

  /** 31. Vergi sinifi olusturur. */
  createClass: (dto: TaxClassUpsertDto) => api.post<TaxClassDto>('/api/admin/tax/classes', dto),

  /** 32. Vergi sinifi gunceller. */
  updateClass: (id: number, dto: TaxClassUpsertDto) =>
    api.put<TaxClassDto>(`/api/admin/tax/classes/${id}`, dto),

  /** 33. Vergi sinifi siler. */
  deleteClass: (id: number) => api.deleteCommand(`/api/admin/tax/classes/${id}/delete`),
};

export const adminCustomersApi = {
  /** 34. Musteri listesi; harcama ve siparis sayisiyla. */
  search: (query: CustomerQueryDto = {}) =>
    api.get<PagedResult<CustomerListItemDto>>('/api/admin/customers', query),

  /** Musteri gruplari (Perakende, Toptan, VIP vb.). */
  groups: () => api.get<CustomerGroupDto[]>('/api/admin/customers/groups'),

  /** 35. Musteri karti: adresler, siparis ozeti, notlar. */
  getById: (userId: string) => api.get<CustomerDetailDto>(`/api/admin/customers/${userId}`),

  /** 36. Musteri grubunu ve personel notunu gunceller. */
  update: (userId: string, dto: UpdateCustomerAdminDto) =>
    api.putCommand(`/api/admin/customers/${userId}`, dto),

  /** 37. Hesabi askiya alir veya yeniden acar. */
  setActive: (userId: string, dto: SetUserActiveDto) =>
    api.postCommand(`/api/admin/customers/${userId}/active`, dto),
};

export const adminStaffApi = {
  /** 38. Personel listesi. */
  search: (query: PagedRequest = {}) =>
    api.get<PagedResult<StaffUserDto>>('/api/admin/staff', query),

  /** 39. Personel olusturur; parola yerine kurulum baglantisi gonderilir. */
  create: (dto: CreateStaffUserDto) => api.post<StaffUserDto>('/api/admin/staff', dto),

  /** 40. Rolleri gunceller; guvenlik damgasi degisir, oturumlar duser. */
  updateRoles: (userId: string, dto: UpdateUserRolesDto) =>
    api.putCommand(`/api/admin/staff/${userId}/roles`, dto),

  /** 41. Personeli pasife alir. */
  setActive: (userId: string, dto: SetUserActiveDto) =>
    api.postCommand(`/api/admin/staff/${userId}/active`, dto),

  /** 42. Tum oturumlarini aninda kapatir. */
  forceLogout: (userId: string) => api.postCommand(`/api/admin/staff/${userId}/force-logout`),

  /** 43. Roller ve her rolun izinleri. */
  roles: () => api.get<RoleDto[]>('/api/admin/staff/roles'),
};

export const adminDashboardApi = {
  /** 44. Ozet kartlari: ciro, siparis, sepet ortalamasi, iade. */
  summary: (from?: string, to?: string) =>
    api.get<DashboardSummaryDto>('/api/admin/dashboard/summary', { from, to }),

  /** 45. Satis grafigi (gun / hafta / ay kirilimi). */
  salesTrend: (from: string, to: string, granularity: 'day' | 'week' | 'month' = 'day') =>
    api.get<SalesTrendPointDto[]>('/api/admin/dashboard/sales-trend', { from, to, granularity }),

  /** 46. En cok satan urunler. */
  topProducts: (from?: string, to?: string, take = 10) =>
    api.get<TopProductDto[]>('/api/admin/dashboard/top-products', { from, to, take }),

  /** En cok satan kategoriler. */
  topCategories: (from?: string, to?: string, take = 10) =>
    api.get<TopCategoryDto[]>('/api/admin/dashboard/top-categories', { from, to, take }),

  /** Son siparisler. */
  recentOrders: (take = 10) =>
    api.get<RecentOrderSummaryDto[]>('/api/admin/dashboard/recent-orders', { take }),

  /** Terk edilen sepetler. */
  abandonedCarts: (take = 50) =>
    api.get<AbandonedCartDetailDto[]>('/api/admin/dashboard/abandoned-carts', { take }),

  /** Manuel terk edilen sepet e-postasi gonderir. */
  sendAbandonedCartEmail: (cartId: number) =>
    api.postCommand(`/api/admin/dashboard/abandoned-carts/${cartId}/send-email`),

  /** Bekleyen tum terk edilen sepetlere toplu e-posta gonderir. */
  sendAllAbandonedCartEmails: () =>
    api.postCommand('/api/admin/dashboard/abandoned-carts/send-all-emails'),

  /** 47. Stogu azalan urun uyarilari. */
  lowStock: (take = 20) => api.get<LowStockAlertDto[]>('/api/admin/dashboard/low-stock', { take }),
};

export const adminSettingsApi = {
  /** 48. Magaza ayarlari; gruba gore filtrelenebilir. */
  getAll: (group?: string) => api.get<StoreSettingDto[]>('/api/admin/settings', { group }),

  /** 49. Ayarlari toplu gunceller. */
  update: (dto: UpdateSettingsDto) => api.putCommand('/api/admin/settings', dto),

  /** SMTP test e-postasi gonderir. */
  sendTestEmail: (toEmail: string) =>
    api.postCommand('/api/admin/settings/test-email', { toEmail }),

  /** Tum e-posta sablonlarini getirir. */
  emailTemplates: () => api.get<EmailTemplateDto[]>('/api/admin/settings/email-templates'),

  /** Tekil e-posta sablonu getirir. */
  getEmailTemplate: (id: number) =>
    api.get<EmailTemplateDto>(`/api/admin/settings/email-templates/${id}`),

  /** Yeni ozel e-posta sablonu olusturur. */
  createEmailTemplate: (dto: EmailTemplateUpsertDto) =>
    api.post<EmailTemplateDto>('/api/admin/settings/email-templates', dto),

  /** E-posta sablonunu gunceller. */
  updateEmailTemplate: (id: number, dto: EmailTemplateUpsertDto) =>
    api.post<EmailTemplateDto>(`/api/admin/settings/email-templates/${id}`, dto),

  /** E-posta sablonunu fabrika varsayilanina sifirlar. */
  resetEmailTemplate: (id: number) =>
    api.post<EmailTemplateDto>(`/api/admin/settings/email-templates/${id}/reset`),

  /** Ozel e-posta sablonunu siler. */
  deleteEmailTemplate: (id: number) =>
    api.deleteCommand(`/api/admin/settings/email-templates/${id}/delete`),

  /** Secili e-posta sablonunu ornek verilerle test olarak gonderir. */
  sendTemplateTestEmail: (id: number, dto: TestEmailTemplateRequestDto) =>
    api.postCommand(`/api/admin/settings/email-templates/${id}/test`, dto),
};

export const adminBulkApi = {
  /** 50. Toplu islem gecmisi. */
  search: (query: PagedRequest = {}) =>
    api.get<PagedResult<BulkOperationDto>>('/api/admin/bulk', query),

  /** 51. Tek islemin durumu ve satir bazli hata raporu. */
  status: (publicId: string) => api.get<BulkOperationDto>(`/api/admin/bulk/${publicId}`),

  /** 52. Urunleri CSV olarak disa aktarir (arka planda calisir). */
  exportProducts: (filter: AdminProductQueryDto = {}) =>
    api.post<BulkOperationDto>('/api/admin/bulk/export/products', undefined, {
      params: filter,
    }),

  /** 53. Siparisleri CSV olarak disa aktarir. */
  exportOrders: (filter: OrderQueryDto = {}) =>
    api.post<BulkOperationDto>('/api/admin/bulk/export/orders', undefined, {
      params: filter,
    }),

  /** 54. Urun CSV'si ice aktarir. */
  importProducts: (file: File) => uploadCsv('/api/admin/bulk/import/products', file),

  /** 55. Stok CSV'si ice aktarir. */
  importInventory: (file: File) => uploadCsv('/api/admin/bulk/import/inventory', file),

  /** 56. Fiyatlari toplu gunceller (yuzde / tutar / sabit). */
  updatePrices: (dto: BulkPriceUpdateDto) =>
    api.post<BulkOperationDto>('/api/admin/bulk/prices', dto),

  /** 57. Devam eden toplu islemi iptal eder. */
  cancel: (publicId: string) => api.postCommand(`/api/admin/bulk/${publicId}/cancel`),
};

async function uploadCsv(url: string, file: File): Promise<BulkOperationDto> {
  const form = new FormData();
  form.append('file', file);
  return api.post<BulkOperationDto>(url, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export const adminReportsApi = {
  sales: (period = 'monthly', from?: string, to?: string) =>
    api.get<SalesReportDto>('/api/admin/reports/sales', { period, from, to }),

  products: (from?: string, to?: string) =>
    api.get<ProductReportDto>('/api/admin/reports/products', { from, to }),

  customers: (from?: string, to?: string) =>
    api.get<CustomerReportDto>('/api/admin/reports/customers', { from, to }),

  funnel: (from?: string, to?: string) =>
    api.get<ConversionFunnelDto>('/api/admin/reports/funnel', { from, to }),
};

